import React, { useMemo, useState } from 'react';
import { Users, Globe, Award, Send, Plus, X, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { languagePairs } from '../data/content';
import { civilities } from '../data/quoteFormOptions';
import { useCatalogLanguages } from '../data/subServicesStore';
import { flagEmoji } from '../utils/langFlags';
import assetUrl from '../assetUrl';
import './TranslatorPage.css';

const SPECIALTY_KEYS = ['legal', 'medical', 'academic', 'commercial', 'technical'];

const LANGUAGE_OPTIONS = Array.from(
  new Set(languagePairs.flatMap((lp) => [lp.from, lp.to])),
);

function pairKey(from, to) {
  return `${from}→${to}`;
}

export default function TranslatorPage() {
  const { t } = useLanguage();
  const tr = t.translator;
  const { languages: catalogLangs } = useCatalogLanguages();
  // Langues actives du catalogue — repli sur les paires statiques hors-ligne
  const langOptions = catalogLangs.length
    ? catalogLangs.map((l) => ({
        value: l.nameFr || l.code,
        label: [flagEmoji(l.flag), l.nameFr || l.code].filter(Boolean).join(' '),
      }))
    : LANGUAGE_OPTIONS.map((n) => ({ value: n, label: n }));
  const [sent, setSent] = useState(false);
  const [fromLang, setFromLang] = useState('');
  const [toLang, setToLang] = useState('');
  const [pairs, setPairs] = useState([]);
  const [pairError, setPairError] = useState(false);

  const perks = [
    { icon: Globe, text: tr.perk1 },
    { icon: Award, text: tr.perk2 },
    { icon: Users, text: tr.perk3 },
  ];

  const canAdd = fromLang && toLang && fromLang !== toLang
    && !pairs.some((p) => p.from === fromLang && p.to === toLang);

  const toOptions = useMemo(
    () => langOptions.filter((lang) => lang.value !== fromLang),
    [fromLang, langOptions],
  );

  function addPair() {
    if (!canAdd) return;
    setPairs((prev) => [...prev, { from: fromLang, to: toLang }]);
    setFromLang('');
    setToLang('');
    setPairError(false);
  }

  function removePair(from, to) {
    setPairs((prev) => prev.filter((p) => !(p.from === from && p.to === to)));
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!e.target.civility.value) {
      setPairError(true);
      return;
    }
    if (pairs.length === 0) {
      setPairError(true);
      return;
    }
    setSent(true);
  }

  return (
    <>
      <header className="translator-hero">
        <div
          className="translator-hero-photo"
          style={{ backgroundImage: `url(${assetUrl('/images/home-zigzag-2.jpg')})` }}
          aria-hidden="true"
        />
        <div className="translator-hero-shade" aria-hidden="true" />
        <div className="container translator-hero-inner">
          <h1>{tr.title}</h1>
          <p>{tr.subtitle}</p>
        </div>
      </header>
      <section className="section translator-section">
        <div className="container">
          <div className="translator-grid">
            <div className="translator-perks">
              {perks.map(({ icon: Icon, text }) => (
                <div key={text} className="translator-perk">
                  <Icon size={24} />
                  <span>{text}</span>
                </div>
              ))}
            </div>
            <form className="translator-form card" onSubmit={handleSubmit}>
              {sent ? (
                <div className="contact-success">{t.contact.success}</div>
              ) : (
                <>
                  <div className="form-group">
                    <label htmlFor="tr-civility">{tr.civility || 'Civilité'}</label>
                    <select id="tr-civility" name="civility" required>
                      <option value="">{tr.choose || '—'}</option>
                      {civilities.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="tr-name">{tr.name}</label>
                    <input id="tr-name" name="name" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="tr-email">{tr.email}</label>
                    <input id="tr-email" name="email" type="email" required />
                  </div>

                  <div className="form-group translator-pairs-group">
                    <label>{tr.langPairs}</label>
                    <p className="translator-pairs-hint">{tr.langPairsHint}</p>

                    <div className="translator-pair-builder">
                      <div className="translator-pair-selects">
                        <label className="translator-pair-field">
                          <span>{tr.fromLang}</span>
                          <select
                            value={fromLang}
                            onChange={(e) => {
                              setFromLang(e.target.value);
                              if (e.target.value === toLang) setToLang('');
                            }}
                            aria-label={tr.fromLang}
                          >
                            <option value="">{tr.selectLang}</option>
                            {langOptions.map((lang) => (
                              <option key={lang.value} value={lang.value}>{lang.label}</option>
                            ))}
                          </select>
                        </label>

                        <span className="translator-pair-arrow" aria-hidden="true">
                          <ArrowRight size={16} />
                        </span>

                        <label className="translator-pair-field">
                          <span>{tr.toLang}</span>
                          <select
                            value={toLang}
                            onChange={(e) => setToLang(e.target.value)}
                            aria-label={tr.toLang}
                            disabled={!fromLang}
                          >
                            <option value="">{tr.selectLang}</option>
                            {toOptions.map((lang) => (
                              <option key={lang.value} value={lang.value}>{lang.label}</option>
                            ))}
                          </select>
                        </label>
                      </div>

                      <button
                        type="button"
                        className="btn btn-outline translator-add-pair"
                        onClick={addPair}
                        disabled={!canAdd}
                      >
                        <Plus size={16} />
                        {tr.addPair}
                      </button>
                    </div>

                    {pairs.length > 0 && (
                      <ul className="translator-pair-list">
                        {pairs.map(({ from, to }) => (
                          <li key={pairKey(from, to)} className="translator-pair-chip">
                            <span>{from}</span>
                            <ArrowRight size={12} aria-hidden="true" />
                            <span>{to}</span>
                            <button
                              type="button"
                              className="translator-pair-remove"
                              onClick={() => removePair(from, to)}
                              aria-label={`${from} → ${to}`}
                            >
                              <X size={14} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {pairError && pairs.length === 0 && (
                      <p className="translator-pair-error" role="alert">{tr.pairRequired}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="tr-spec">{tr.specialization}</label>
                    <select id="tr-spec" name="specialization" required>
                      <option value="">{tr.choose}</option>
                      {SPECIALTY_KEYS.map((key) => (
                        <option key={key} value={key}>{tr.specialties[key]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="tr-exp">{tr.experience}</label>
                    <input id="tr-exp" name="experience" type="number" min="0" required />
                  </div>
                  <div className="form-group">
                    <label htmlFor="tr-cv">{tr.cv}</label>
                    <input id="tr-cv" name="cv" type="file" accept=".pdf" />
                  </div>
                  <button type="submit" className="btn btn-primary translator-submit">
                    <Send size={18} />
                    {tr.apply}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
